import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  DomainService,
  GetSenderInfoResponse,
  GetSenderInfoResponseInterface,
  Sender,
  SenderDomain,
  SenderType,
} from '@vendasta/email';
import { SubscriptionList } from '@vendasta/rx-utils';
import { combineLatest, firstValueFrom, forkJoin, Observable, of, Subscription, throwError, BehaviorSubject, Subject } from 'rxjs';
import { catchError, map, shareReplay, tap, filter, distinctUntilChanged, take, takeUntil, concatMap } from 'rxjs/operators';
import { AddressAPIService } from '@vendasta/address';
import { SelectInputOptionInterface } from '@vendasta/galaxy/input';
import { EmailValidators } from '../../../../email-form-validators';
import { DomainCacheService } from '../../domain-cache.service';
import { DomainsDialogComponent, Result as DialogResult } from '../../domains-dialog/domains-dialog.component';
import { SavedState, UNSAVED_DATA_HANDLER_TOKEN, UnsavedDataHandler } from '../dependencies';
import { SelectedDomain } from './selected-domain';
import { DefaultDomainService } from '../../../src/default-domain.service';
import { SelectedDomainFactoryService } from './selected-domain-factory.service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { EmailApiService, GlobalParticipantType } from '@vendasta/conversation';
import { Clipboard } from '@angular/cdk/clipboard';
import { EmailForwardingInfoComponent } from './email-forwarding-info/email-forwarding-info.component';
import { AccountGroupService, MarketingInfoOperation, ProjectionFilter, UpdateOperations } from '@galaxy/account-group';

export interface SenderInfoFormValue {
  senderName: string;
  useOwn: boolean;
  senderEmailUsername: string;
  replyTo: string;
  usePhysicalAddressOverride: boolean;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateProv: string;
  postalCode: string;
  country: string;
}

export interface SaveData {
  domain: string;
  preferredDisplayName: string;
  preferredEmailUsername: string;
  senderName: string;
  replyTo: string;
  usePhysicalAddressOverride: boolean;
}

@Component({
  selector: 'email-sender-info-form',
  templateUrl: './sender-info-form.component.html',
  styleUrls: ['./sender-info-form.component.scss'],
  standalone: false,
})
export class SenderInfoFormComponent implements OnInit, OnChanges, OnDestroy {
  @Output() readonly domainChangeRequested = new EventEmitter<string>();
  @Output() readonly invalidDomainChangeRequested = new EventEmitter<string>();
  @Output() readonly authorizeRequested = new EventEmitter<void>();

  @Input() readonly sender: Sender;

  @Input() readonly senderInfo: GetSenderInfoResponse;

  /**
   * domainOverride sets the domain that is displayed in this component. It
   * is displayed INSTEAD OF the sender's actual domain. (e.g. prior to saving)
   */
  @Input() domainOverride: string;

  domains$: Observable<SenderDomain[]>;

  senderInfoForm: FormGroup<{
    senderName: FormControl<string>;
    useOwn: FormControl<boolean>;
    senderEmailUsername: FormControl<string>;
    replyTo: FormControl<string>;
    usePhysicalAddressOverride: FormControl<boolean>;
    addressLine1: FormControl<string>;
    addressLine2: FormControl<string>;
    city: FormControl<string>;
    stateProv: FormControl<string>;
    postalCode: FormControl<string>;
    country: FormControl<string>;
  }> = new FormGroup({
    senderName: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    useOwn: new FormControl<boolean>(false, { nonNullable: true }),
    senderEmailUsername: new FormControl<string>('', { nonNullable: true, validators: [Validators.required, EmailValidators.emailUsername()] }),
    replyTo: new FormControl<string>('', { nonNullable: true, validators: [EmailValidators.fullEmailAddress()] }),
    usePhysicalAddressOverride: new FormControl<boolean>(false, { nonNullable: true }),
    addressLine1: new FormControl<string>('', { nonNullable: true }),
    addressLine2: new FormControl<string>('', { nonNullable: true }),
    city: new FormControl<string>('', { nonNullable: true }),
    stateProv: new FormControl<string>('', { nonNullable: true }),
    postalCode: new FormControl<string>('', { nonNullable: true }),
    country: new FormControl<string>('', { nonNullable: true }),
  });

  forwardingAddress: Promise<string>;
  protected isCopied = false;
  private readonly emailApiService = inject(EmailApiService);

  selectedDomain$: Observable<SelectedDomain>;
  selectedDomainName$: Observable<string>;
  saveSenderInfoRequest$: Observable<any> = of(null);
  saveDomainRequest$: Observable<any> = of(null);

  useOwn: boolean;
  showAlertBanner = true;

  showForwardingAddress: boolean;

  countryOptions$: Observable<SelectInputOptionInterface[]>;
  stateProvOptions$$ = new BehaviorSubject<SelectInputOptionInterface[]>([]);
  private readonly onDestroy = new Subject<void>();

  private readonly subs = SubscriptionList.new();
  private notifier: Subscription | null = null;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    @Inject(UNSAVED_DATA_HANDLER_TOKEN) private readonly unsavedHandler: UnsavedDataHandler,
    private readonly dialog: MatDialog,
    private readonly domainService: DomainCacheService,
    private defaultDomainService: DefaultDomainService,
    private readonly selectedDomainFactory: SelectedDomainFactoryService,
    private readonly domains: DomainService,
    private readonly alerts: SnackbarService,
    private clipboard: Clipboard,
    private readonly addressService: AddressAPIService,
    private readonly accountGroupService: AccountGroupService,
  ) {
    this.subs.add(this.senderInfoForm.get('useOwn').valueChanges, (value) => this.handleUseOwnChanged(value));
    this.subs.add(
      this.senderInfoForm.get('usePhysicalAddressOverride').valueChanges,
      (value) => this.handlePhysicalAddressOverrideChanged(value),
    );
  }

  ngOnInit(): void {
    this.showForwardingAddress = this.sender.type === SenderType.SENDER_TYPE_BUSINESS;
    this.domains$ = this.domainService.listAllDomains(this.sender.type, this.sender.id).pipe(map((r) => r.domains));
    this.forwardingAddress = this.getForwardingAddress();
    this.initializeCountryOptions();
    this.setupCountryChangeHandler();
    this.loadAccountGroupMarketingInfo();
  }

  ngOnChanges(): void {
    this.updateFormValuesIfUnset(this.senderInfo);
    if (this.domainOverride) {
      this.senderInfoForm.get('useOwn').setValue(true);
      const senderInfo: GetSenderInfoResponseInterface = {
        domain: this.domainOverride,
        isPlatformAuthorized: true,
      };
      this.selectedDomain$ = this.selectedDomainFactory.fromSenderInfo(this.sender.type, senderInfo, true);
      this.selectedDomainName$ = this.selectedDomain$.pipe(map((d) => d.domain));
    }
  }

  private initializeCountryOptions(): void {
    this.countryOptions$ = this.addressService.listAllCountryOptions().pipe(
      take(1),
      map((countries) => {
        if (!countries) {
          return [];
        }
        return countries
          .sort((a, b) => {
            const nameA = a.name.toUpperCase();
            const nameB = b.name.toUpperCase();
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          })
          .map((country) => ({
            value: country?.code || '',
            label: country?.name || '',
          }));
      }),
      shareReplay(1),
    );
  }

  private setupCountryChangeHandler(): void {
    this.senderInfoForm.get('country').valueChanges
      .pipe(
        takeUntil(this.onDestroy),
        filter((country) => !!country),
        distinctUntilChanged(),
        tap((country) => this.updateStateProvOptions(country)),
      )
      .subscribe();
  }

  private updateStateProvOptions(country: string, state?: string): void {
    this.addressService
      .getCountryConfiguration(country, undefined)
      .pipe(
        map((countryConfig) =>
          countryConfig?.zones?.map((zone) => ({
            value: zone?.code || '',
            label: zone?.name || '',
          })),
        ),
        catchError(() => []),
        tap((stateProvs: SelectInputOptionInterface[]) => this.stateProvOptions$$.next(stateProvs || [])),
        tap((stateProvs: SelectInputOptionInterface[]) => {
          if (state && stateProvs && stateProvs.findIndex((sp) => sp.value === state) >= 0) {
            this.senderInfoForm.get('stateProv').setValue(state);
          } else {
            this.senderInfoForm.get('stateProv').setValue('');
          }
        }),
        take(1),
      )
      .subscribe();
  }

  private handleUseOwnChanged(useOwnDomain: boolean): void {
    this.useOwn = useOwnDomain;
    this.selectedDomain$ = this.selectedDomainFactory.fromSenderInfo(this.sender.type, this.senderInfo, useOwnDomain);
    this.selectedDomainName$ = this.selectedDomain$.pipe(map((d) => d.domain));
    this.cdr.detectChanges();
  }

  private handlePhysicalAddressOverrideChanged(useOverride: boolean): void {
    const addressFields = ['addressLine1', 'city', 'stateProv', 'postalCode', 'country'];
    addressFields.forEach((fieldName) => {
      const control = this.senderInfoForm.get(fieldName);
      if (useOverride) {
        control.setValidators([Validators.required]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private loadAccountGroupMarketingInfo(): void {
    this.accountGroupService
      .getMulti([this.sender.id], new ProjectionFilter({ marketingInfo: true }))
      .pipe(
        take(1),
        catchError(() => of([])),
        map((accountGroups) => accountGroups[0]?.marketingInfo),
        tap((marketingInfo: any) => {
          if (!marketingInfo) {
            return;
          }

          const useOverride = marketingInfo.useOverride || false;
          this.senderInfoForm.get('usePhysicalAddressOverride').setValue(useOverride, { emitEvent: false });

          if (useOverride && marketingInfo.addressOverride) {
            this.populateAddressFields(marketingInfo.addressOverride);
          }
        }),
      )
      .subscribe();
  }

  private populateAddressFields(addressOverride: any): void {
    if (addressOverride.address) {
      this.senderInfoForm.get('addressLine1').setValue(addressOverride.address, { emitEvent: false });
    }
    if (addressOverride.address2) {
      this.senderInfoForm.get('addressLine2').setValue(addressOverride.address2, { emitEvent: false });
    }
    if (addressOverride.city) {
      this.senderInfoForm.get('city').setValue(addressOverride.city, { emitEvent: false });
    }
    if (addressOverride.state) {
      this.senderInfoForm.get('stateProv').setValue(addressOverride.state, { emitEvent: false });
    }
    if (addressOverride.zip) {
      this.senderInfoForm.get('postalCode').setValue(addressOverride.zip, { emitEvent: false });
    }
    if (addressOverride.country) {
      this.senderInfoForm.get('country').setValue(addressOverride.country, { emitEvent: false });
      this.updateStateProvOptions(addressOverride.country, addressOverride.state);
    }
  }

  public handleAuthorizeDomain(): void {
    this.authorizeRequested.emit();
  }

  private updateFormValuesIfUnset(info: GetSenderInfoResponseInterface): void {
    this.notifier?.unsubscribe();
    this.senderInfoForm.get('useOwn').setValue(!info.isDefaultForSenderType);
    const senderName = this.senderInfoForm.get('senderName');
    if (!senderName.value) {
      senderName.setValue(info.preferredEmailDisplayName);
    }
    const senderEmailUsername = this.senderInfoForm.get('senderEmailUsername');
    if (!senderEmailUsername.value) {
      senderEmailUsername.setValue(info.preferredEmailUsername);
    }
    const replyTo = this.senderInfoForm.get('replyTo');
    if (!replyTo.value) {
      replyTo.setValue(info.preferredReplyToEmailAddress);
    }
    this.notifier = this.senderInfoForm.valueChanges.subscribe(() =>
      this.unsavedHandler.notifyStateChanged(SavedState.HAS_UNSAVED_CHANGES),
    );
  }

  handleSaveRequested(isDomainSave: boolean): void {
    if (this.senderInfoForm.invalid) {
      return;
    }

    const saveData = this.getSaveData();
    const saveRequest = saveData.pipe(
      concatMap((saveData) => {
        const domainUpdate$ = this.domains.changeForSender(
          this.sender.type,
          this.sender.id,
          saveData.domain,
          saveData.preferredEmailUsername,
          saveData.replyTo,
          saveData.senderName,
        );

        const accountGroupUpdate$ = this.createAccountGroupUpdate();

        return forkJoin([domainUpdate$, accountGroupUpdate$]);
      }),
      tap(() => this.alerts.openSuccessSnack('DOMAIN_AUTHORIZATION.SENDER_INFO_CARD.UPDATE_SUCCESS')),
      catchError((err) => {
        this.alerts.openErrorSnack('DOMAIN_AUTHORIZATION.SENDER_INFO_CARD.UPDATE_FAILURE');
        return throwError(() => err);
      }),
      tap(() => this.unsavedHandler.notifyStateChanged(SavedState.SAFE_TO_NAVIGATE_AWAY)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    if (isDomainSave) {
      this.saveDomainRequest$ = saveRequest;
    } else {
      this.saveSenderInfoRequest$ = saveRequest;
    }
  }

  private createAccountGroupUpdate(): Observable<any> {
    const usePhysicalAddressOverride = this.senderInfoForm.get('usePhysicalAddressOverride').value;
    const updateOperations = new UpdateOperations();
    const marketingInfoOperation = new MarketingInfoOperation();

    if (usePhysicalAddressOverride) {
      marketingInfoOperation.useOverride = true;
      marketingInfoOperation.addressOverride = this.buildAddressOverride();
    } else {
      marketingInfoOperation.useOverride = false;
    }

    updateOperations.addUpdateOperation(marketingInfoOperation);
    return this.accountGroupService.bulkUpdate(this.sender.id, updateOperations);
  }

  private buildAddressOverride(): any {
    return {
      address: this.senderInfoForm.get('addressLine1').value,
      address2: this.senderInfoForm.get('addressLine2').value || undefined,
      city: this.senderInfoForm.get('city').value,
      state: this.senderInfoForm.get('stateProv').value || undefined,
      zip: this.senderInfoForm.get('postalCode').value || undefined,
      country: this.senderInfoForm.get('country').value,
    };
  }

  getSaveData(): Observable<SaveData> {
    // TODO: Prevent saving if form is not valid?
    const useOwnDomain = this.senderInfoForm.get('useOwn').value;
    return combineLatest([this.defaultDomainService.getDefaultDomain$(this.sender.type), this.selectedDomain$]).pipe(
      map(([defaultDomain, selectedDomain]) => {
        return {
          domain: useOwnDomain ? selectedDomain.domain : defaultDomain,
          preferredDisplayName: this.senderInfoForm.get('senderName').value,
          preferredEmailUsername: this.senderInfoForm.get('senderEmailUsername').value,
          senderName: this.senderInfoForm.get('senderName').value,
          replyTo: this.senderInfoForm.get('replyTo').value,
          usePhysicalAddressOverride: this.senderInfoForm.get('usePhysicalAddressOverride').value,
        } as SaveData;
      }),
    );
  }

  async openChooseDomainDialog(): Promise<void> {
    const dialog = this.dialog.open(DomainsDialogComponent, {
      data: this.sender,
      maxHeight: '600px',
    });
    const result: DialogResult = await firstValueFrom(dialog.afterClosed());
    switch (result.choiceType) {
      case 'new':
        this.authorizeRequested.emit();
        break;
      case 'valid':
        this.domainChangeRequested.emit(result.newDomain);
        break;
      case 'invalid':
        this.invalidDomainChangeRequested.emit(result.newDomain);
        break;
    }
  }

  closeAlert() {
    this.showAlertBanner = false;
  }

  async copyClicked() {
    this.isCopied = true;
    this.clipboard.copy(await this.forwardingAddress);
    setTimeout(() => {
      this.isCopied = false;
    }, 5000);
  }

  async getForwardingAddress(): Promise<string> {
    return await firstValueFrom(
      this.emailApiService
        .getOwnerEmail({
          subjectParticipant: {
            participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
            internalParticipantId: this.sender?.id ?? '',
          },
        })
        .pipe(
          map((emailRes) => {
            return emailRes.forwardingEmail ?? '';
          }),
          catchError(() => of('')),
        ),
    );
  }

  openLearnMoreModal(): void {
    this.dialog.open(EmailForwardingInfoComponent, { width: '600px' });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
    this.subs.destroy();
  }
}
